/**
 * Coerce a SpacetimeDB timestamp to millis-since-epoch.
 *
 * The fork SDK hands back a `Timestamp` instance carrying
 * `__timestamp_micros_since_unix_epoch__`; older builds handed back a `Date`,
 * a raw number/bigint, or `{ micros }` / `{ microsSinceUnixEpoch }` objects.
 * Probe every known shape; unknown shapes fall back to 0.
 *
 * Extracted from useLiveSessions (the one copy that handled the real shape) —
 * NOTE: useMyAssignments/useUsers/Leadership/ReviewQueue still carry abridged
 * local copies that miss the `__timestamp_micros…` probe and silently return
 * 0 for current-SDK timestamps; migrate them here when touched.
 */
export function toMillis(t: unknown): number {
  if (t == null) return 0;
  if (typeof t === 'number') return t;
  if (typeof t === 'bigint') return Number(t / 1000n);
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'object') {
    const anyT = t as {
      micros?: bigint;
      microsSinceUnixEpoch?: bigint;
      __timestamp_micros_since_unix_epoch__?: bigint;
      getTime?: () => number;
      toDate?: () => Date;
    };
    if (typeof anyT.__timestamp_micros_since_unix_epoch__ === 'bigint')
      return Number(anyT.__timestamp_micros_since_unix_epoch__ / 1000n);
    if (typeof anyT.microsSinceUnixEpoch === 'bigint')
      return Number(anyT.microsSinceUnixEpoch / 1000n);
    if (typeof anyT.micros === 'bigint') return Number(anyT.micros / 1000n);
    if (typeof anyT.toDate === 'function') return anyT.toDate().getTime();
    if (typeof anyT.getTime === 'function') return anyT.getTime();
  }
  return 0;
}
